export const MAX_FILE_MB = 100
export const REDACTION_DPI = 150

const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024
const PREVIEW_MAX_WIDTH = 720
const JPEG_QUALITY = 0.92

export interface PdfPagePreview {
  pageNumber: number
  width: number
  height: number
  dataUrl: string
}

export interface RedactionInspection {
  pageCount: number
  pages: PdfPagePreview[]
}

export interface RedactionBox {
  id: string
  pageNumber: number
  x: number
  y: number
  width: number
  height: number
}

export interface RedactionProgress {
  current: number
  total: number
}

interface PdfJsModule {
  GlobalWorkerOptions: { workerSrc: string }
  getDocument: (src: { data: ArrayBuffer }) => { promise: Promise<PdfJsDocument> }
}

interface PdfJsDocument {
  numPages: number
  getPage: (n: number) => Promise<PdfJsPage>
  destroy: () => Promise<void>
}

interface PdfJsPage {
  getViewport: (opts: { scale: number }) => PdfJsViewport
  render: (opts: { canvasContext: CanvasRenderingContext2D; viewport: PdfJsViewport }) => {
    promise: Promise<void>
    cancel: () => void
  }
  cleanup: () => void
}

interface PdfJsViewport {
  width: number
  height: number
}

export function validateFileSize(file: File): boolean {
  return file.size <= MAX_FILE_BYTES
}

export function redactedFilename(originalName: string): string {
  const dotIndex = originalName.lastIndexOf('.')
  const base = dotIndex !== -1 ? originalName.slice(0, dotIndex) : originalName
  return `${base}-redacted.pdf`
}

export async function inspectPdfForRedaction(file: File): Promise<RedactionInspection> {
  const pdfjs = await loadPdfJs()
  const bytes = await file.arrayBuffer()
  const doc = await pdfjs.getDocument({ data: bytes }).promise

  try {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('canvas-unavailable')

    const pages: PdfPagePreview[] = []

    for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber++) {
      const page = await doc.getPage(pageNumber)
      const baseViewport = page.getViewport({ scale: 1 })
      const scale = Math.min(1.5, PREVIEW_MAX_WIDTH / baseViewport.width)
      const viewport = page.getViewport({ scale })

      canvas.width = Math.ceil(viewport.width)
      canvas.height = Math.ceil(viewport.height)
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      await page.render({ canvasContext: ctx, viewport }).promise

      pages.push({
        pageNumber,
        width: baseViewport.width,
        height: baseViewport.height,
        dataUrl: canvas.toDataURL('image/jpeg', 0.82),
      })

      page.cleanup()
    }

    canvas.width = 0
    canvas.height = 0

    return {
      pageCount: doc.numPages,
      pages,
    }
  } finally {
    await doc.destroy().catch(() => {})
  }
}

export async function redactPdf(options: {
  file: File
  boxes: RedactionBox[]
  signal: AbortSignal
  onProgress: (progress: RedactionProgress) => void
}): Promise<{ blob: Blob; pageCount: number }> {
  const { file, boxes, signal, onProgress } = options
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError')

  const [pdfjs, pdfLib] = await Promise.all([loadPdfJs(), import('pdf-lib')])
  const bytes = await file.arrayBuffer()
  const sourceDoc = await pdfjs.getDocument({ data: bytes }).promise
  const outputDoc = await pdfLib.PDFDocument.create()

  try {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d', { alpha: false })
    if (!ctx) throw new Error('canvas-unavailable')

    onProgress({ current: 0, total: sourceDoc.numPages })

    const renderScale = REDACTION_DPI / 72

    for (let pageNumber = 1; pageNumber <= sourceDoc.numPages; pageNumber++) {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError')

      const page = await sourceDoc.getPage(pageNumber)
      const baseViewport = page.getViewport({ scale: 1 })
      const viewport = page.getViewport({ scale: renderScale })
      canvas.width = Math.ceil(viewport.width)
      canvas.height = Math.ceil(viewport.height)

      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      const task = page.render({ canvasContext: ctx, viewport })
      const onAbort = () => task.cancel()
      signal.addEventListener('abort', onAbort, { once: true })
      try {
        await task.promise
      } finally {
        signal.removeEventListener('abort', onAbort)
      }

      ctx.fillStyle = '#000000'
      for (const box of boxes.filter((item) => item.pageNumber === pageNumber)) {
        ctx.fillRect(
          Math.round(box.x * canvas.width),
          Math.round(box.y * canvas.height),
          Math.ceil(box.width * canvas.width),
          Math.ceil(box.height * canvas.height),
        )
      }

      const imageBlob = await canvasToJpegBlob(canvas)
      const imageBytes = await imageBlob.arrayBuffer()
      const embeddedPage = await outputDoc.embedJpg(imageBytes)
      const outputPage = outputDoc.addPage([baseViewport.width, baseViewport.height])
      outputPage.drawImage(embeddedPage, {
        x: 0,
        y: 0,
        width: baseViewport.width,
        height: baseViewport.height,
      })

      page.cleanup()
      onProgress({ current: pageNumber, total: sourceDoc.numPages })
    }

    canvas.width = 0
    canvas.height = 0

    const out = await outputDoc.save()
    return {
      blob: new Blob([out], { type: 'application/pdf' }),
      pageCount: sourceDoc.numPages,
    }
  } finally {
    await sourceDoc.destroy().catch(() => {})
  }
}

async function loadPdfJs(): Promise<PdfJsModule> {
  const pdfjs = (await import('pdfjs-dist')) as unknown as PdfJsModule
  const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl
  return pdfjs
}

function canvasToJpegBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob)
        return
      }
      reject(new Error('encode-failed'))
    }, 'image/jpeg', JPEG_QUALITY)
  })
}
